'use strict';

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var ttiPolyfill = _interopDefault(require('tti-polyfill'));

var EmulatedPerformance = /** @class */ (function () {
    function EmulatedPerformance(config) {
        this.config = config;
    }
    /**
     * When performance API is not available
     * returns Date.now that is limited to one-millisecond resolution.
     */
    EmulatedPerformance.prototype.now = function () {
        return Date.now() / 1000;
    };
    EmulatedPerformance.prototype.mark = function (metricName, type) {
        // Timeline won't be marked
    };
    EmulatedPerformance.prototype.measure = function (metricName, metrics) {
        return this.getDurationByMetric(metricName, metrics);
    };
    /**
     * First Paint is essentially the paint after which
     * the biggest above-the-fold layout change has happened.
     * Uses setTimeout to retrieve FCP
     */
    EmulatedPerformance.prototype.firstContentfulPaint = function (cb) {
        var _this = this;
        setTimeout(function () {
            cb(_this.getFirstPaint());
        });
    };
    /**
     * Get the duration of the timing metric or -1 if there a measurement has
     * not been made by now() fallback.
     */
    EmulatedPerformance.prototype.getDurationByMetric = function (metricName, metrics) {
        var duration = metrics[metricName].end - metrics[metricName].start;
        return duration || 0;
    };
    /**
     * http://msdn.microsoft.com/ff974719
     * developer.mozilla.org/en-US/docs/Web/API/PerformanceTiming/navigationStart
     */
    EmulatedPerformance.prototype.getFirstPaint = function () {
        var navTiming = window.performance.timing;
        var performancePaintTiming = {
            duration: 0,
            entryType: 'paint',
            name: 'first-contentful-paint',
            startTime: 0
        };
        if (navTiming && navTiming.navigationStart !== 0) {
            performancePaintTiming.startTime = Date.now() - navTiming.navigationStart;
        }
        return [performancePaintTiming];
    };
    return EmulatedPerformance;
}());

var Performance = /** @class */ (function () {
    function Performance(config) {
        this.config = config;
        this.timeToInteractiveDuration = 0;
        this.ttiPolyfill = ttiPolyfill;
    }
    /**
     * True if the browser supports the Navigation Timing API,
     * User Timing API and the PerformanceObserver Interface.
     * In Safari, the User Timing API (performance.mark()) is not available,
     * so the DevTools timeline will not be annotated with marks.
     * Support: developer.mozilla.org/en-US/docs/Web/API/Performance/mark
     * Support: developer.mozilla.org/en-US/docs/Web/API/PerformanceObserver
     */
    Performance.supported = function () {
        return window.performance && !!performance.now && !!performance.mark;
    };
    /**
     * For now only Chrome fully support the PerformanceObserver interface
     * and the entryType "paint".
     * Firefox 58: https://bugzilla.mozilla.org/show_bug.cgi?id=1403027
     */
    Performance.supportedPerformanceObserver = function () {
        return window.chrome && 'PerformanceObserver' in window;
    };
    /**
     * True if the browser supports the PerformanceLongTaskTiming interface.
     * Support: developer.mozilla.org/en-US/docs/Web/API/PerformanceLongTaskTiming
     */
    Performance.supportedLongTask = function () {
        return 'PerformanceLongTaskTiming' in window;
    };
    /**
     * When performance API available
     * returns a DOMHighResTimeStamp, measured in milliseconds, accurate to five
     * thousandths of a millisecond (5 microseconds).
     */
    Performance.prototype.now = function () {
        return window.performance.now();
    };
    Performance.prototype.mark = function (metricName, type) {
        var mark = "mark_" + metricName + "_" + type;
        window.performance.mark(mark);
    };
    Performance.prototype.measure = function (metricName, metrics) {
        var startMark = "mark_" + metricName + "_start";
        var endMark = "mark_" + metricName + "_end";
        window.performance.measure(metricName, startMark, endMark);
        return this.getDurationByMetric(metricName, metrics);
    };
    /**
     * First Paint is essentially the paint after which
     * the biggest above-the-fold layout change has happened.
     * PerformanceObserver subscribes to performance events as they happen
     * and respond to them asynchronously.
     * entry.name will be either 'first-paint' or 'first-contentful-paint'
     */
    Performance.prototype.firstContentfulPaint = function (cb) {
        this.perfObserver = new PerformanceObserver(this.performanceObserverCb.bind(this, cb));
        this.perfObserver.observe({ entryTypes: ['paint'] });
    };
    /**
     * The polyfill exposes a getFirstConsistentlyInteractive() method,
     * which returns a promise that resolves with the TTI value.
     *
     * The getFirstConsistentlyInteractive() method accepts an optional
     * startTime configuration option, allowing you to specify a lower bound
     * for which you know your app cannot be interactive before.
     * By default the polyfill uses DOMContentLoaded as the start time,
     * but it's often more accurate to use something like the moment your hero elements
     * are visible or the point when you know all your event listeners have been added.
     */
    Performance.prototype.timeToInteractive = function (minValue) {
        return this.ttiPolyfill.getFirstConsistentlyInteractive({ minValue: minValue });
    };
    /**
     * Get the duration of the timing metric or -1 if there a measurement has
     * not been made by the User Timing API
     */
    Performance.prototype.getDurationByMetric = function (metricName, metrics) {
        var entry = this.getMeasurementForGivenName(metricName);
        if (entry && entry.entryType === 'measure') {
            return entry.duration;
        }
        return -1;
    };
    /**
     * Return the last PerformanceEntry objects for the given name.
     */
    Performance.prototype.getMeasurementForGivenName = function (metricName) {
        var entries = window.performance.getEntriesByName(metricName);
        return entries[entries.length - 1];
    };
    Performance.prototype.performanceObserverCb = function (cb, entryList) {
        var _this = this;
        var entries = entryList.getEntries();
        cb(entries);
        entries.forEach(function (performancePaintTiming) {
            if (_this.config.firstContentfulPaint &&
                performancePaintTiming.name === 'first-contentful-paint') {
                _this.perfObserver.disconnect();
            }
        });
    };
    return Performance;
}());

/*!
 * Perfume.js v0.8.0 (http://zizzamia.github.io/perfume)
 * Copyright 2018 The Perfume Authors (https://github.com/Zizzamia/perfume.js/graphs/contributors)
 * Licensed under MIT (https://github.com/Zizzamia/perfume.js/blob/master/LICENSE)
 * @license
 */
var Perfume = /** @class */ (function () {
    function Perfume(options) {
        if (options === void 0) { options = {}; }
        var _this = this;
        this.config = {
            firstContentfulPaint: false,
            firstPaint: false,
            timeToInteractive: false,
            analyticsLogger: undefined,
            googleAnalytics: {
                enable: false,
                timingVar: 'name'
            },
            logPrefix: '⚡️ Perfume.js:',
            logging: true,
            warning: false
        };
        this.firstPaintDuration = 0;
        this.firstContentfulPaintDuration = 0;
        this.timeToInteractiveDuration = 0;
        this.metrics = {};
        this.logMetric = 'Please provide a metric name';
        this.config = Object.assign({}, this.config, options);
        // Init performance implementation
        this.perf = Performance.supported()
            ? new Performance(this.config)
            : new EmulatedPerformance(this.config);
        this.timeToInteractivePromise = new Promise(function (resolve, reject) {
            // Init First Contentful Paint
            if (Performance.supportedPerformanceObserver()) {
                _this.perf.firstContentfulPaint(function (entries) {
                    _this.firstContentfulPaintCb(entries, resolve, reject);
                });
            }
            else {
                _this.perfEmulated = new EmulatedPerformance(_this.config);
                _this.perfEmulated.firstContentfulPaint(function (entries) {
                    _this.firstContentfulPaintCb(entries, resolve, reject);
                });
            }
        });
    }
    Perfume.prototype.observeTimeToInteractive = function () {
        return this.timeToInteractivePromise;
    };
    /**
     * Start performance measurement
     */
    Perfume.prototype.start = function (metricName) {
        if (!this.checkMetricName(metricName)) {
            return;
        }
        if (this.metrics[metricName]) {
            this.logWarn(this.config.logPrefix, 'Recording already started.');
            return;
        }
        this.metrics[metricName] = {
            end: 0,
            start: this.perf.now()
        };
        this.perf.mark(metricName, 'start');
    };
    /**
     * End performance measurement
     */
    Perfume.prototype.end = function (metricName) {
        if (!this.checkMetricName(metricName)) {
            return;
        }
        if (!this.metrics[metricName]) {
            this.logWarn(this.config.logPrefix, 'Recording already stopped.');
            return;
        }
        this.metrics[metricName].end = this.perf.now();
        this.perf.mark(metricName, 'end');
        var duration = this.perf.measure(metricName, this.metrics);
        this.log(metricName, duration);
        delete this.metrics[metricName];
        this.sendTiming(metricName, duration);
        return duration;
    };
    /**
     * End performance measurement after first paint from the beging of it
     */
    Perfume.prototype.endPaint = function (metricName) {
        var _this = this;
        return new Promise(function (resolve) {
            setTimeout(function () {
                var duration = _this.end(metricName);
                resolve(duration);
            });
        });
    };
    /**
     * Coloring Text in Browser Console
     */
    Perfume.prototype.log = function (metricName, duration) {
        if (!this.config.logging) {
            return;
        }
        if (!metricName) {
            this.logWarn(this.config.logPrefix, this.logMetric);
            return;
        }
        var durationMs = duration.toFixed(2);
        var style = 'color: #ff6d00;font-size:12px;';
        var text = "%c " + this.config.logPrefix + " " + metricName + " " + durationMs + " ms";
        window.console.log(text, style);
    };
    Perfume.prototype.checkMetricName = function (metricName) {
        if (metricName) {
            return true;
        }
        this.logWarn(this.config.logPrefix, this.logMetric);
        return false;
    };
    Perfume.prototype.firstContentfulPaintCb = function (entries, resolve, reject) {
        var _this = this;
        var firstContentfulPaintDuration;
        entries.forEach(function (performancePaintTiming) {
            if (_this.config.firstPaint && performancePaintTiming.name === 'first-paint') {
                _this.logFCP(performancePaintTiming.startTime, 'First Paint', 'firstPaint');
            }
            if (_this.config.firstContentfulPaint &&
                performancePaintTiming.name === 'first-contentful-paint') {
                _this.logFCP(performancePaintTiming.startTime, 'First Contentful Paint', 'firstContentfulPaint');
            }
            if (performancePaintTiming.name === 'first-contentful-paint') {
                firstContentfulPaintDuration = performancePaintTiming.startTime;
            }
        });
        if (Performance.supported() &&
            Performance.supportedPerformanceObserver() &&
            Performance.supportedLongTask() &&
            this.config.timeToInteractive &&
            firstContentfulPaintDuration) {
            this.perf
                .timeToInteractive(firstContentfulPaintDuration)
                .then(function (time) {
                resolve(time);
                _this.timeToInteractiveCb(time);
            })
                .catch(reject);
        }
    };
    Perfume.prototype.timeToInteractiveCb = function (timeToInteractive) {
        this.timeToInteractiveDuration = timeToInteractive;
        if (this.timeToInteractiveDuration) {
            this.log('Time to interactive', this.timeToInteractiveDuration);
        }
        this.sendTiming('timeToInteractive', this.timeToInteractiveDuration);
    };
    Perfume.prototype.logFCP = function (duration, logText, metricName) {
        if (metricName === 'firstPaint') {
            this.firstPaintDuration = duration;
        }
        if (metricName === 'firstContentfulPaint') {
            this.firstContentfulPaintDuration = duration;
        }
        this.log(logText, duration);
        this.sendTiming(metricName, duration);
    };
    /**
     * Sends the User timing measure to Google Analytics.
     * ga('send', 'timing', [timingCategory], [timingVar], [timingValue])
     * timingCategory: metricName
     * timingVar: googleAnalytics.timingVar
     * timingValue: The value of duration rounded to the nearest integer
     */
    Perfume.prototype.sendTiming = function (metricName, duration) {
        if (this.config.analyticsLogger) {
            this.config.analyticsLogger(metricName, duration);
        }
        if (!this.config.googleAnalytics.enable) {
            return;
        }
        if (!window.ga) {
            this.logWarn(this.config.logPrefix, 'Google Analytics has not been loaded');
            return;
        }
        var durationInteger = Math.round(duration);
        window.ga('send', 'timing', metricName, this.config.googleAnalytics.timingVar, durationInteger);
    };
    Perfume.prototype.logWarn = function (prefix, message) {
        if (!this.config.warning || !this.config.logging) {
            return;
        }
        window.console.warn(prefix, message);
    };
    return Perfume;
}());

module.exports = Perfume;
//# sourceMappingURL=perfume.js.map
